import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    Switch,
    Button,
    CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';
import { getSubmission, wsAssignLockSubmission, wsAssignUnlockSubmission } from '../../../../../../Utils/apiService';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import KenLoader from '../../../../../../GlobalComponents/KenLoader';

interface Student {
    id: string;
    userid: string;
    username: string;
    status: 'submitted' | 'not-submitted';
    gradingstatus: string;
    grade: number | null;
    timemodified: string | null;
    lockstatus: boolean;
    contactId: string;
}

interface SubmissionPlugin {
    type: string;
    name: string;
    fileareas?: any[];
}

interface Submission {
    id: string;
    userid: string;
    attemptnumber: number;
    timecreated: number | null;
    timemodified: number | null;
    status: string;
    groupid: number;
    assignment: number;
    latest: number;
    plugins: SubmissionPlugin[];
    gradingstatus: string;
    usermodified: number;
    timemarked: number;
    graded: string;
    statusname: string;
    ObtainedGrade: number | null;
    lockstatus?: boolean;
    username: string;
}

interface Assignment {
    id: string;
    cmid: string;
    course: string;
    name: string;
    nosubmissions: string;
    submissiondrafts: string;
    sendnotifications: string;
    sendlatenotifications: string;
    sendstudentnotifications: string;
    duedate: number;
    allowsubmissionsfromdate: number;
    grade: number;
    timemodified: number;
    completionsubmit: string;
    cutoffdate: number;
    gradingduedate: number;
    teamsubmission: string;
    requireallteammemberssubmit: string;
    teamsubmissiongroupingid: string;
    blindmarking: string;
    hidegrader: string;
    revealidentities: string;
    attemptreopenmethod: string;
    maxattempts: string;
    markingworkflow: string;
    markingallocation: string;
    requiresubmissionstatement: string;
    preventsubmissionnotingroup: string;
    submissionstatement: string;
    submissionstatementformat: string;
    configs: any[];
    intro: string;
    introformat: number;
    assignmenttype: string;
    activitylink: string;
    submissions: Submission[];
    assignmentname: string;
}

interface GradingSummary {
    participantcount: number;
    submissionsenabled: boolean;
    submissiondrafts: boolean;
    submissiondraftscount: number;
    submissionsneedgradingcount: number;
    submissionssubmittedcount: number;
    submissionsneedgradingtotal: number;
}

interface SubmissionResponse {
    assignments: Assignment[];
    warnings: any[];
    gradingsummary?: GradingSummary;
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        maxWidth: '100%',
        background: '#fff',
        padding: "2rem",
        borderRadius: 12,
        height: 'calc(100vh - 160px)',
    },
    tableHead: {
        backgroundColor: '#E6F1F8',
        borderBottom: '0.5px solid #172E7D',
    },
    tableRow: {
        '&:nth-of-type(odd)': {
            backgroundColor: '#f9fafc'
        },
        '& td': {
            borderBottom: 'none',
            fontSize: '14px',
            fontWeight: '400',
            color: '#585A64',
            textAlign: 'left',
        },
    },
    tableTitle: {
        color: '#193389 !important',
        fontSize: '14px',
        fontWeight: '500',
        textAlign: "left",
    },
    searchField: {
        width: 250,
        borderRadius: 12,
    },
    statusPercentage: {
        padding: theme.spacing(1, 2),
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#e8f5e9',
        borderRadius: theme.spacing(1)
    },
    submittedStatus: {
        padding: theme.spacing(1, 2),
        backgroundColor: '#fbe9e7',
        borderRadius: 20,
        display: 'inline-block'
    },
    submittedText: {
        fontWeight: 600,
        color: '#d84315'
    },
    notSubmittedStatus: {
        padding: theme.spacing(1, 2),
        backgroundColor: '#ffebee',
        borderRadius: 20,
        display: 'inline-block'
    },
    notSubmittedText: {
        fontWeight: 600,
        color: '#c62828'
    },
    gradeButton: {
        backgroundColor: '#e0e0e0',
        color: '#757575',
        '&:hover': {
            backgroundColor: '#bdbdbd'
        }
    },
    title: {
        marginBottom: theme.spacing(2)
    },
    noRecord: {
        padding: theme.spacing(2),
        color: '#9e9e9e'
    },
    headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2)
    },
    scrollableBody: {
        overflow: 'auto',
    },
    tableContainer: {
        maxHeight: 'calc(100vh - 280px)', // Adjust this value based on your layout
        overflow: 'hidden',
        borderRadius: 1,
        boxShadow: 'none',
    },
}));


const StudentAssignmentTracker: React.FC = () => {
    const classes = useStyles();
    const location = useLocation();
    const [students, setStudents] = useState<Student[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [submissionHeading, setSubmissionHeading] = useState<string>('My students');
    const [gradingSummary, setGradingSummary] = useState<GradingSummary | null>(null);
    const [assignPercentage, setAssignPercentage] = useState<string | null>('0.00');
    const [lockAction, setLockAction] = useState<boolean>(false);
    const [cmid, setCmid] = useState<string>(
        location.state?.cmid
    );
    const navigate = useNavigate();
    console.log(cmid, "cmid")

    // Fetch submission data
    useEffect(() => {
        const fetchSubmissions = async () => {
            try {
                setLoading(true);
                const payload = {
                    cmid: cmid,
                    status: '',
                    since: 0,
                    before: 0,
                };

                const response: SubmissionResponse = await getSubmission(payload);

                if (response && response.assignments && response.assignments.length > 0) {
                    setSubmissionHeading(response.assignments[0].assignmentname);
                    setGradingSummary(response.gradingsummary || null);

                    // Transform the submissions to match our Student interface
                    const transformedData: Student[] = response.assignments[0].submissions.map((item: Submission) => {
                        return {
                            id: item.id,
                            userid: item.userid,
                            username: item.username,
                            status: item.status === 'submitted' ? 'submitted' : 'not-submitted',
                            gradingstatus: item.gradingstatus === 'graded'
                                ? 'Graded'
                                : item.gradingstatus === 'notgraded'
                                    ? 'Not graded'
                                    : item.gradingstatus,
                            grade: item.ObtainedGrade,
                            timemodified: item.timemodified !== null
                                ? moment.unix(item.timemodified).format('LLLL')
                                : null,
                            lockstatus: item.lockstatus || false,
                            contactId: item.userid
                        };
                    });

                    setStudents(transformedData);
                    setLockAction(false);
                }
            } catch (err) {
                console.error('Error fetching submissions:', err);
                setError('Failed to load student submissions. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchSubmissions();
    }, [cmid, lockAction]);

    // Calculate submission percentage when gradingSummary changes
    useEffect(() => {
        if (gradingSummary !== null) {
            setAssignPercentage(
                ((gradingSummary.submissionssubmittedcount / gradingSummary.participantcount) * 100).toFixed(2)
            );
        } else {
            // Default to 0.00% when no data
            setAssignPercentage('0.00');
        }
    }, [gradingSummary]);

    // Filter students based on search query
    const filteredStudents = students.filter(student =>
        student.username.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Handle lock submission
    const handleLockSubmission = async (contactId: string) => {
        try {
            const payload = {
                cmid: cmid,
                contactid: contactId,
            };
            await wsAssignLockSubmission(payload);
            setLockAction(true);
        } catch (err) {
            console.error('Error locking submission:', err);
            // Optionally handle error in UI
        }
    };

    // Handle unlock submission
    const handleUnlockSubmission = async (contactId: string) => {
        try {
            const payload = {
                cmid: cmid,
                contactid: contactId,
            };
            await wsAssignUnlockSubmission(payload);
            setLockAction(true);
        } catch (err) {
            console.error('Error unlocking submission:', err);
            // Optionally handle error in UI
        }
    };

    // Handle lock/unlock toggle
    const handleLockToggle = (contactId: string, currentLockStatus: boolean) => {
        if (currentLockStatus) {
            handleUnlockSubmission(contactId);
        } else {
            handleLockSubmission(contactId);
        }
    };

    // Check if grade button should be disabled (not submitted)
    const isGradeButtonDisabled = (userId: string): boolean => {
        const student = students.find(s => s.userid === userId);
        return student ? student.status !== 'submitted' : true;
    };
    const onGradeClick = (contactId: string) => {
        navigate('/assignment-original', {
            state: {
                cmid: cmid,
                contactId: contactId,
                groupid: 0,
                submittedUsers: students.filter(s => s.status === 'submitted'),
                submissionHeading: submissionHeading,
            }
        });
    };

    return (
        <Box className={classes.root}>
            {/* New layout with heading, search and percentage all in one row */}
            <Box className={classes.headerRow}>
                <Typography style={{ fontSize: "16px", fontWeight: "500" }}>My Students</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Search"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            sx: {
                                borderRadius: "12px",
                                "& fieldset": {
                                    borderRadius: "12px",
                                },
                                "&:hover fieldset": {
                                    border: '0.5px solid #B2B3B7 !important',
                                },
                                "&.Mui-focused fieldset": {
                                    border: '0.5px solid #B2B3B7 !important',
                                },
                                "& input": {
                                    borderRadius: "12px",
                                }
                            }
                        }}
                        className={classes.searchField}
                    />


                    <Paper
                        elevation={0}
                        sx={{
                            p: 1,
                            px: 2,
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#e8f5e9',
                            borderRadius: 2
                        }}
                    >
                        <Typography variant="body2" sx={{ mr: 1, color: '#2e7d32' }}>
                            Assignment Submission Percentage:
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                            {assignPercentage}%
                        </Typography>
                    </Paper>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <KenLoader />
                </Box>
            ) : error ? (
                <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
                    <Typography>{error}</Typography>
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{
                        mb: 4,
                        borderRadius: 1,
                        boxShadow: 'none',
                        maxHeight: 'calc(100vh - 260px)', // Adjust based on your layout
                        position: 'relative'
                    }}
                >
                    <Table stickyHeader>
                        <TableHead className={classes.tableHead}>
                            <TableRow>
                                <TableCell className={classes.tableTitle}>Student name</TableCell>
                                <TableCell className={classes.tableTitle}>Status</TableCell>
                                <TableCell className={classes.tableTitle}>Grading status</TableCell>
                                <TableCell className={classes.tableTitle}>Grades</TableCell>
                                <TableCell className={classes.tableTitle}>Modified date</TableCell>
                                <TableCell className={classes.tableTitle}>Lock/unlock</TableCell>
                                <TableCell className={classes.tableTitle}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredStudents.length > 0 ? (
                                filteredStudents.map((student) => (
                                    <TableRow key={student.id} className={classes.tableRow}>
                                        <TableCell>{student.username}</TableCell>
                                        <TableCell>
                                            {student.status === 'submitted' ? (
                                                <Box sx={{ backgroundColor: '#f9ede6', borderRadius: '20px', display: 'inline-block', padding: '6px 16px' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#B49353' }}>Submitted</Typography>
                                                </Box>
                                            ) : (
                                                <Box sx={{ backgroundColor: '#F1BFBF', borderRadius: '20px', display: 'inline-block', padding: '6px 16px' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '14px', color: '#591414' }}>Not - submitted</Typography>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>{student.gradingstatus}</TableCell>
                                        <TableCell>{student.grade !== null ? student.grade : 0}</TableCell>
                                        <TableCell>{student.timemodified || 'N/A'}</TableCell>
                                        <TableCell align="center">
                                            <Switch
                                                checked={student.lockstatus}
                                                onChange={() => handleLockToggle(student.contactId, student.lockstatus)}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Button
                                                variant="contained"
                                                size="small"
                                                disabled={isGradeButtonDisabled(student.userid)}
                                                onClick={() => onGradeClick(student.userid)}
                                                sx={{
                                                    background: isGradeButtonDisabled(student.userid) ? '#E8EBF3' : '#193389',
                                                    color: isGradeButtonDisabled(student.userid) ? '#9e9e9e' : 'white',
                                                    '&:hover': {
                                                        background: isGradeButtonDisabled(student.userid) ? '#E8EBF3' : '#0e1e4d'
                                                    }
                                                }}
                                            >
                                                Grade
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography className={classes.noRecord}>No records found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default StudentAssignmentTracker;